/**
 * The engine's client for the staff side (docs/PLAN-2026-09-17-admin-engine.md §3).
 * The bearer is the engine's own session token for a staff account made in the
 * engine (`auth_users.role` editor, admin or owner), kept in sessionStorage.
 * Radar depends on nothing else (founder, 2026-10-02: Drive is extracted).
 */
import { createEngineClient, EngineError } from "@gamo/api-client";

const TOKEN_KEY = "radar.engine.pass";

const baseUrl = (import.meta.env["VITE_ENGINE_URL"] as string | undefined) ?? "https://engine.gogamo.africa";
/** Radar is a client of the engine like the apps; it identifies itself so the engine's app checks pass. Seeded by `make engine-seed`. */
const appId = (import.meta.env["VITE_ENGINE_APP_ID"] as string | undefined) ?? "01M2FHC7K6HC14SSXCVG5YAAAK";

export const engineToken = {
  get: (): string | null => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string | null): void => {
    try {
      if (token) sessionStorage.setItem(TOKEN_KEY, token);
      else sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* private mode */
    }
  },
};

const listeners = new Set<() => void>();
export function onEngineUnauthorized(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const engine = createEngineClient({
  baseUrl,
  appId,
  getToken: () => engineToken.get(),
  onUnauthorized: () => {
    engineToken.set(null);
    for (const fn of listeners) fn();
  },
});

export const ENGINE_BASE = baseUrl;
export { EngineError };

/** One sentence from any failure; never a stack trace. */
export function describeEngineError(err: unknown): string {
  if (err instanceof EngineError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
